import { setItemPruning } from '../utils/bounded'

/**
 * Client-side unread tracking. The messaging contract has NO unread field on
 * MessagingConversation, so we derive it: a conversation is unread when its last
 * message is inbound and newer (by `seq`) than the last seq the operator has
 * seen. The seen map and the derived "conversations with unread" count are
 * persisted in localStorage so the nav badge survives navigation/reload and
 * stays live across panes via a tiny external store (useSyncExternalStore).
 */

const SEEN_PREFIX = 'messaging:lastReadSeq:'
/** Kept only when the origin's quota is full — see `setItemPruning`. */
const KEEP_CONVERSATIONS = 500
const COUNT_KEY = 'messaging:unreadConversationCount'

type Listener = () => void

const listeners = new Set<Listener>()
const conversationReadListeners = new Set<Listener>()
let version = 0

function emit(): void {
  version += 1
  listeners.forEach((listener) => listener())
}

export function getUnreadVersion(): number {
  return version
}

export function subscribeUnread(listener: Listener): () => void {
  listeners.add(listener)

  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith(SEEN_PREFIX) || event.key === COUNT_KEY) {
      emit()
    }
  }

  window.addEventListener('storage', onStorage)

  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function subscribeConversationRead(listener: Listener): () => void {
  conversationReadListeners.add(listener)
  return () => conversationReadListeners.delete(listener)
}

export function getLastReadSeq(conversationId: string): number {
  try {
    const value = localStorage.getItem(SEEN_PREFIX + conversationId)
    return value ? Number(value) : 0
  } catch {
    return 0
  }
}

export function markConversationRead(conversationId: string, seq: number): void {
  if (seq <= getLastReadSeq(conversationId)) {
    return
  }

  // Prunes and retries under a full quota rather than silently giving up —
  // one key per conversation ever opened, never otherwise removed. A failure
  // that survives the retry is genuinely cosmetic here (the conversation
  // re-reads as unread after a reload), unlike the clientSeq case, so there
  // is nothing to warn about: the in-memory bump below still updates the UI.
  setItemPruning(SEEN_PREFIX + conversationId, String(seq), SEEN_PREFIX, KEEP_CONVERSATIONS)
  emit()
  conversationReadListeners.forEach((listener) => listener())
}

export function getUnreadConversationCount(): number {
  try {
    const value = localStorage.getItem(COUNT_KEY)
    return value ? Number(value) : 0
  } catch {
    return 0
  }
}

/** Updated by the global unread feed whenever it recomputes. */
export function setUnreadConversationCount(count: number): void {
  if (count === getUnreadConversationCount()) {
    return
  }

  try {
    localStorage.setItem(COUNT_KEY, String(count))
  } catch {
    // Ignore persistence failures.
  }

  emit()
}
